import type { AuthProfile } from "@/contexts/AuthContext"
import { getLocalPassportSummary, type EqPassportSummary } from "@/services/prooftreeBackend"
import { getSupabaseClient } from "@/services/supabaseClient"
import * as Linking from "expo-linking"
import { Platform } from "react-native"

export type PublicPassportVisibilityMode = "public" | "link_only" | "disabled"

export type PublicPassportSettings = {
  enabled: boolean
  visibilityMode: PublicPassportVisibilityMode
  shareKey: string
  displayName: string
  headline: string
  bio: string
}

export type PublicPassportProof = {
  id: string
  skillId: string
  skillName: string
  domainName: string
  checkpointLevel: number
  title: string
  verifiedAt: string | null
  evidenceCount: number
}

export type PublicPassportCard = {
  id: string
  displayName: string
  avatarUrl: string | null
  updatedAt: string | null
  profile: PublicPassportSettings
  summary: EqPassportSummary & {
    publicProofCount: number
    latestVerifiedAt: string | null
  }
  keyProofs: PublicPassportProof[]
}

const PUBLIC_PASSPORT_ROUTE_PATTERN = /(?:^|\/)(?:profile|p)\/@?([^/?#]+)/i

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : ""
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback
}

function asNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string") {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  return 0
}

function asVisibilityMode(value: unknown, enabled: boolean): PublicPassportVisibilityMode {
  if (value === "public" || value === "link_only" || value === "disabled") {
    return value
  }

  return enabled ? "link_only" : "disabled"
}

function decodeShareKey(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function normalizePublicShareKey(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  const routeMatch = trimmed?.match(PUBLIC_PASSPORT_ROUTE_PATTERN)
  const source = routeMatch?.[1] ? decodeShareKey(routeMatch[1]) : trimmed
  const normalized = source
    ?.trim()
    .replace(/^@+/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")

  return normalized ? normalized.slice(0, 64) : null
}

export function generatePublicShareKey(input: {
  displayName?: string | null
  email?: string | null
  userId?: string | null
}) {
  const emailLocalPart = input.email?.split("@")[0]
  return (
    normalizePublicShareKey(input.displayName) ??
    normalizePublicShareKey(emailLocalPart) ??
    normalizePublicShareKey(input.userId?.slice(0, 12)) ??
    `passport-${Date.now()}`
  )
}

export function buildPublicPassportPath(shareKey: string) {
  const normalized = normalizePublicShareKey(shareKey) ?? shareKey
  return `/p/@${normalized}`
}

export function buildLegacyPublicPassportPath(shareKey: string) {
  const normalized = normalizePublicShareKey(shareKey) ?? shareKey
  return `/profile/${normalized}`
}

export function buildPublicPassportUrl(shareKey: string) {
  const path = buildPublicPassportPath(shareKey)

  if (Platform.OS === "web" && typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${path}`
  }

  return Linking.createURL(path)
}

export function getPublicPassportSettings(input: {
  profile: AuthProfile | null
  email?: string | null
  userId?: string | null
}): PublicPassportSettings {
  const settings = asRecord(input.profile?.settings)
  const storedPublicProfile = asRecord(settings?.publicProfile)
  const fallbackDisplayName =
    input.profile?.display_name ?? input.email?.split("@")[0] ?? "EQ builder"
  const enabled = asBoolean(storedPublicProfile?.enabled, false)
  const visibilityMode = asVisibilityMode(storedPublicProfile?.visibilityMode, enabled)
  const shareKey =
    normalizePublicShareKey(asString(storedPublicProfile?.shareKey)) ??
    generatePublicShareKey({
      displayName: fallbackDisplayName,
      email: input.email,
      userId: input.userId ?? input.profile?.id,
    })

  return {
    enabled: visibilityMode !== "disabled",
    visibilityMode,
    shareKey,
    displayName: asString(storedPublicProfile?.displayName) || fallbackDisplayName,
    headline: asString(storedPublicProfile?.headline) || "EMO.coach Passport",
    bio:
      asString(storedPublicProfile?.bio) ||
      "Verified emotional-intelligence growth, EQ skills, and public proof milestones.",
  }
}

export function mergePublicPassportSettings(
  settings: Record<string, unknown> | null | undefined,
  publicProfile: PublicPassportSettings,
) {
  const currentSettings = asRecord(settings) ?? {}
  const shareKey =
    normalizePublicShareKey(publicProfile.shareKey) ||
    generatePublicShareKey({ displayName: publicProfile.displayName })

  return {
    ...currentSettings,
    publicProfile: {
      ...publicProfile,
      enabled: publicProfile.visibilityMode !== "disabled",
      shareKey,
    },
  }
}

function parseSummary(value: unknown): PublicPassportCard["summary"] {
  const row = asRecord(value)
  const local = getLocalPassportSummary()

  return {
    skillCount: asNumber(row?.skillCount) || local.skillCount,
    levelCount: asNumber(row?.levelCount) || local.levelCount,
    verifiedLevelCount: asNumber(row?.verifiedLevelCount),
    proofCount: asNumber(row?.proofCount),
    verifiedProofCount: asNumber(row?.verifiedProofCount),
    publicProofCount: asNumber(row?.publicProofCount),
    activeTaskCount: asNumber(row?.activeTaskCount),
    progressPercent: asNumber(row?.progressPercent),
    latestVerifiedAt: asString(row?.latestVerifiedAt) || null,
  }
}

function parseKeyProofs(value: unknown): PublicPassportProof[] {
  if (!Array.isArray(value)) return []

  return value
    .map((item) => {
      const row = asRecord(item)
      const id = asString(row?.id)
      const title = asString(row?.title)

      if (!row || !id || !title) return null

      return {
        id,
        skillId: asString(row.skillId),
        skillName: asString(row.skillName),
        domainName: asString(row.domainName),
        checkpointLevel: asNumber(row.checkpointLevel),
        title,
        verifiedAt: asString(row.verifiedAt) || null,
        evidenceCount: asNumber(row.evidenceCount),
      }
    })
    .filter((proof): proof is PublicPassportProof => Boolean(proof))
}

export async function publishEqPassport(input: {
  profile: AuthProfile | null
  email?: string | null
  userId?: string | null
}) {
  const client = getSupabaseClient()

  if (!client) {
    throw new Error("Supabase is not configured for this build.")
  }

  const { data: userResult, error: userError } = await client.auth.getUser()
  if (userError || !userResult.user) {
    throw new Error("Sign in before publishing a Passport link.")
  }

  const { data: storedProfile, error: profileError } = await client
    .from("profiles")
    .select("id,email,display_name,avatar_url,settings")
    .eq("id", userResult.user.id)
    .single()

  if (profileError) throw profileError

  const profile = (storedProfile as AuthProfile | null) ?? input.profile
  const current = getPublicPassportSettings({
    profile,
    email: userResult.user.email ?? input.email,
    userId: userResult.user.id,
  })
  const nextPublicProfile: PublicPassportSettings = {
    ...current,
    enabled: true,
    visibilityMode: current.visibilityMode === "public" ? "public" : "link_only",
    displayName:
      current.displayName || profile?.display_name || userResult.user.email?.split("@")[0] || "",
  }
  const settings = mergePublicPassportSettings(profile?.settings, nextPublicProfile)

  const { data: updatedProfile, error: updateError } = await client
    .from("profiles")
    .update({ settings })
    .eq("id", userResult.user.id)
    .select("id,email,display_name,avatar_url,settings")
    .single()

  if (updateError) throw updateError

  const publicProfile = getPublicPassportSettings({
    profile: updatedProfile as AuthProfile,
    email: userResult.user.email,
    userId: userResult.user.id,
  })

  return {
    settings: publicProfile,
    url: buildPublicPassportUrl(publicProfile.shareKey),
    path: buildPublicPassportPath(publicProfile.shareKey),
  }
}

export async function resolvePublicPassportCard(
  shareKey: string,
): Promise<PublicPassportCard | null> {
  const normalizedKey = normalizePublicShareKey(shareKey)
  const client = getSupabaseClient()

  if (!normalizedKey || !client) {
    return null
  }

  const { data, error } = await client.rpc("get_eq_public_passport", {
    public_share_key: normalizedKey,
  })

  if (error) throw error

  const row = Array.isArray(data) ? data[0] : data
  const publicProfile = asRecord(row?.public_profile)
  if (!row || !publicProfile) return null

  const profile = getPublicPassportSettings({
    profile: {
      id: String(row.id),
      email: null,
      display_name: asString(row.display_name),
      avatar_url: asString(row.avatar_url) || null,
      settings: { publicProfile },
    },
  })

  if (!profile.enabled || profile.visibilityMode === "disabled") return null

  return {
    id: String(row.id),
    displayName: profile.displayName || asString(row.display_name),
    avatarUrl: asString(row.avatar_url) || null,
    updatedAt: asString(row.updated_at) || null,
    profile,
    summary: parseSummary(row.passport_summary),
    keyProofs: parseKeyProofs(row.key_proofs),
  }
}
